import { html } from 'lit-element';
import { PieMathRenderer } from '../classroom-tool-extensions/common/PieMathRenderer.js';
import {
  defineElement,
  importMathRendering,
} from '../classroom-tool-extensions/classifier/utils.js';
import {
  bubbleStyles,
  choiceValueStyles,
  responseStyles,
} from '../cssUtils.js';
import { getIcon, processEscapedMarkup, renderBubble } from '../utils.js';
import { CustomLitElement } from '../CustomLitElement.js';

/**
 * @element drag-in-the-blank-response
 * @prop {Object} model - The model for the pie
 * @prop {Object} session - The response session
 * @cssprop --pie-live-session-background-color - change the background color
 * @cssprop --pie-live-session-text-color - change the text color
 * @cssprop --pie-live-session-bubble-text-color - change the text color
 * @cssprop --pie-live-session-font-family - change the font family
 * @cssprop --pie-live-session-font-size-large - change the font size
 * @cssprop --pie-live-session-response-height - change the height (must be valid, eg: 100px, 10em )
 * @cssprop --pie-live-session-response-width - change the width (must be valid, eg: 100px, 10em )
 */
export class DragInTheBlankResponse extends CustomLitElement {
  static get properties() {
    return {
      model: { type: Object, attribute: false },
      session: { type: Array, attribute: false },
      hoveredChoice: { type: Object },
    };
  }

  createRenderRoot() {
    return this;
  }

  set hoveredChoice(val) {
    const oldVal = this._hoveredChoice;

    if (!oldVal || !val || oldVal.value !== val.value) {
      this._hoveredChoice = val;
      this.requestUpdate('hoveredChoice', oldVal);
    }
  }

  get hoveredChoice() {
    return this._hoveredChoice;
  }

  constructor() {
    super();
    this._hoveredChoice = null;
  }

  mouseover(e, value) {
    const item = e.target.closest('.choice-value');

    if (item && item.getBoundingClientRect) {
      const bounds = item.getBoundingClientRect();

      this.hoveredChoice = {
        value,
        top: bounds.top + bounds.height / 2,
        left: bounds.left + bounds.width,
      };
    }
  }

  mouseout() {
    this.hoveredChoice = null;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    defineElement('math-rendering-element', PieMathRenderer);

    if (!this.mathRenderingModuleUrlImported) {
      importMathRendering(this.mathRenderingModuleUrl);

      this.mathRenderingModuleUrlImported = true;
    }
  }

  _renderResponse() {
    const { correctResponse, choices } = this.model || {};
    const { value } = this.session || {};

    if (!value || !correctResponse) return html``;
    const correctResponseKeys = Object.keys(correctResponse) || [];

    return correctResponseKeys.map(v => {
      const choice = choices.find(ch => ch.id === value[v]);

      return html`<div
        class="choice-value choice-content"
        @mouseover=${e => this.mouseover(e, choice && choice.value)}
        @mouseout=${this.mouseout}
      >
        <p class="choice-index">
          ${parseInt(v, 10) + 1}.
          <span class="choice-icon">
            ${!value[v] ? '' : getIcon(correctResponse[v] === value[v])}
          </span>
        </p>
        ${choice &&
        html`<math-rendering-element
          .label="${processEscapedMarkup(choice.value)}"
        ></math-rendering-element>`}
        <div class="choice-overflow"></div>
      </div>`;
    });
  }

  render() {
    return html`
      <style>
        ${bubbleStyles}
          ${choiceValueStyles}
          ${responseStyles}
          .drag-in-the-blank
          span {
          margin: 2px;
        }
      </style>
      <div class="response drag-in-the-blank">
        ${this._renderResponse()} ${renderBubble(this.hoveredChoice)}
      </div>
    `;
  }
}

export default DragInTheBlankResponse;
